import {
  Component,
  computed,
  HostListener,
  inject,
  input,
  model,
  signal,
  TemplateRef,
} from '@angular/core'
import { GroupModel, ProductOption } from '../../models/group-model'
import {
  IMemberModel,
  IScopesExpiry,
  MemberModel,
} from '../../models/member-model'
import {
  DetailsToggleDirective,
  DialogCloseEventWithResult,
  DialogComponent,
  FormDateInputComponent,
  FormInputComponent,
  FormMultiSelectComponent,
  ModalService,
  MultiSelectOptionDirective,
  Sort,
  TableModule,
  TooltipDirective,
} from '@inst-iot/bosch-angular-ui-components'
import { TitleCasePipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { UserManagementService } from '../../service/user-management.service'
import { AuthService } from '../../service/auth.service'
import { ToastService } from '../../service/app-toast.service'
import {
  concat,
  concatMap,
  defer,
  map,
  Observable,
  retry,
  Subscription,
} from 'rxjs'
import { HttpErrorResponse } from '@angular/common/http'
import { RbPopoverWithHoverDirective } from '../directives/rb-popover-with-hover.directive'
import { Permissions } from '../enums/permissions.enum'
import { ToggleStateService } from '../../service/toggle-state.service'

@Component({
  selector: 'app-user-table',
  imports: [
    DialogComponent,
    TableModule,
    TitleCasePipe,
    FormInputComponent,
    FormsModule,
    DetailsToggleDirective,
    TooltipDirective,
    FormMultiSelectComponent,
    MultiSelectOptionDirective,
    RbPopoverWithHoverDirective,
    FormDateInputComponent,
  ],
  templateUrl: './user-table.component.html',
  styleUrl: './user-table.component.scss',
})
export class UserTableComponent {
  public group = input.required<string>()
  public productOption = input.required<ProductOption>()
  public members = input.required<MemberModel[]>()

  private toggleStateService = inject(ToggleStateService)

  getProductToggleKey(): string {
    return `${this.group()}:${this.productOption().name}`
  }

  getProductIsOpen(): boolean {
    return this.toggleStateService.getToggleState(
      this.getProductToggleKey(),
      true,
    )
  }

  onProductToggleChange(isOpen: boolean): void {
    this.toggleStateService.setToggleState(this.getProductToggleKey(), isOpen)
  }

  public mailAddress = model<string>('')
  public searchMember = model<string>('')
  private currentSort = signal<Sort>({ id: 'displayName', direction: '' })

  activeUserId = computed(() => this.authService.activeUserId())
  public filteredMembers = computed<MemberModel[]>(() => {
    const members = this.members() ?? []
    const search = this.searchMember().toLowerCase()
    const sort = this.currentSort()

    // Filter
    const filtered = members.filter((member) => {
      const name = member.displayName?.toLowerCase() || ''
      const mail = member.email?.toLowerCase() || ''
      return name.includes(search) || mail.includes(search)
    })

    // Sort
    if (!sort.id || sort.direction === '') {
      return [...filtered]
    }
    const sorted = [...filtered]
    sorted.sort((a, b) => {
      let aValue = (a as any)[sort.id]
      let bValue = (b as any)[sort.id]
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }
      return (aValue < bValue ? -1 : 1) * (sort.direction === 'asc' ? 1 : -1)
    })
    return sorted
  })
  public isRKeyPressed = signal<boolean>(false)

  //activeUserId: Signal<string> = computed(() => this.authService.activeUserId())
  private updateUserSubscription: Subscription | null = null

  constructor(
    private readonly userManagementService: UserManagementService,
    private readonly authService: AuthService,
    private readonly toasterService: ToastService,
    private modalService: ModalService,
  ) {}

  @HostListener('window:keydown.r', ['$event']) onKeyDown(event: Event) {
    // Beispiel: Reagiere auf spezifische Tasten
    if (!this.isRKeyPressed()) {
      this.isRKeyPressed.set(true)
    }
  }

  @HostListener('window:keyup.r', ['$event']) onKeyUp(event: Event) {
    // Beispiel: Reagiere auf spezifische Tasten
    this.isRKeyPressed.set(false)
  }

  findData(searchText: string): void {
    this.searchMember.set(searchText)
  }

  onSortChange(sort: Sort): void {
    this.currentSort.set(sort)
  }

  emailsCopied = signal<boolean>(false)

  copyEmailsToClipboard(): void {
    const emails = this.members()
      .map((m) => m.email)
      .filter(Boolean)
      .join(';')
    navigator.clipboard.writeText(emails)
    this.emailsCopied.set(true)
    setTimeout(() => this.emailsCopied.set(false), 2000)
  }

  public async onScopeSelectionchange(
    selectedScopes: any[],
    member: MemberModel,
  ) {
    const newSelectedScopes = Object.entries(selectedScopes)
      .filter(([key, value]) => value === true) // Filter out entries with true values
      .map(([key, _]) => key)
    //console.log(`${member.displayName} new scopes: ${newSelectedScopes}`)
    member.scopes = newSelectedScopes

    await this.updateUserWorkflow(member)
  }

  public async onRoleSelectionchange(
    selectedRoles: any[],
    member: MemberModel,
  ) {
    const newSelectedRoles = Object.entries(selectedRoles)
      .filter(([key, value]) => value === true) // Filter out entries with true values
      .map(([key, _]) => key)
    //console.log(`${member.displayName} new scopes: ${newSelectedScopes}`)
    member.roles = newSelectedRoles

    await this.updateUserWorkflow(member)
  }

  private updateOrExcludeBackendScopesExpiry(
    member: IMemberModel,
  ): IMemberModel {
    // exlude backendScopesExpiry from member
    const temp = member.scopes
    if (
      member.scopesExpiry === undefined ||
      (Array.isArray(member.scopesExpiry) && member.scopesExpiry.length === 0)
    )
      return { ...member, scopesExpiry: [] }

    if (
      member.scopesExpiry.some(
        (scopeExpiry) => !member.scopes.includes(scopeExpiry.scope),
      )
    ) {
      // remove all scopes that are no longer granted to this member
      member.scopesExpiry = member.scopesExpiry.filter((scopeExpiry) =>
        member.scopes.includes(scopeExpiry.scope),
      )
    }
    return member
  }

  private async updateUserWorkflow(member: IMemberModel) {
    member.scopes = member.scopes.filter((s) =>
      this.productOption().backendscopes.includes(s),
    )
    member.roles = member.roles.filter((r) =>
      this.productOption().roles.includes(r),
    )

    const updateUser$ = this.getUpdateUserObservable(member)
    const getUsers$ = this.userManagementService.getUser()

    if (this.updateUserSubscription) {
      this.updateUserSubscription.unsubscribe()
      this.updateUserSubscription = null
    }

    this.updateUserSubscription = concat(updateUser$, getUsers$).subscribe({
      next: (response) => {},
      complete: () => {
        this.toasterService.show({
          type: 'success',
          message: `user updated successfully`,
        })
        this.updateUserSubscription = null
      },
      error: (errorResp: HttpErrorResponse | Error) => {
        if (errorResp instanceof HttpErrorResponse) {
          this.toasterService.show({
            type: 'error',
            message: `${errorResp.status}: ${errorResp.error}`,
          })
        } else {
          this.toasterService.show({
            type: 'error',
            message: `Error: ${errorResp.message}`,
          })
        }
        this.updateUserSubscription = null
      },
    })
  }

  private getUpdateUserObservable(member: IMemberModel): Observable<string> {
    const scopes = member.scopes.filter((scope) =>
      this.productOption().backendscopes.includes(scope),
    )
    member.scopes = scopes

    const updateUser$ = this.userManagementService.updateUserInGroup(
      member,
      this.group(),
      this.productOption().name,
    )

    return updateUser$
  }

  inviteUserWorkflow(
    mailAddress: string,
    groupName: string,
    productName: string,
  ) {
    mailAddress = mailAddress.trim().toLowerCase()
    if (mailAddress == '') {
      return
    }
    const inviteUser$ = this.userManagementService.inviteUserInProductAndGroup(
      mailAddress,
      groupName,
      productName,
    )

    const getUsers$ = this.userManagementService.getUser()

    inviteUser$
      .pipe(
        concatMap(() =>
          defer(() =>
            getUsers$.pipe(
              map((groupModel: GroupModel[]) => {
                const group = groupModel.find(
                  (gm: GroupModel) => gm.group == groupName,
                )
                if (!group) {
                  throw new Error('Unable to fetch group')
                }
                if (
                  !group.members.find(
                    (m: MemberModel) => m.email === mailAddress,
                  )
                ) {
                  throw new Error('Failed to fetch new user')
                }
              }),
            ),
          ),
        ),
      )
      .subscribe({
        complete: () => {
          this.mailAddress.set('')
          this.toasterService.show({
            type: 'success',
            message: `User successfully added`,
          })
        },
        error: (erroResp: any) => {
          this.toasterService.show({
            type: 'error',
            message:
              erroResp?.error || erroResp?.message || 'Something went wrong',
          })
        },
      })
  }

  removeUserWorkflow(userId: string, groupName: string, productName: string) {
    const removeUser$ = this.userManagementService.removeUserFromGroup(
      userId,
      groupName,
      productName,
    )

    const getUsers$ = this.userManagementService.getUser()

    return removeUser$
      .pipe(
        concatMap(() => getUsers$),
        map((groupModelArray: GroupModel[]) => {
          const groupModel = groupModelArray.find(
            (g: GroupModel) => g.group === groupName,
          )

          if (!groupModel) {
            throw new Error(`Group ${groupName} not found`)
          }

          const isStillMember = groupModel.members?.find(
            (member) => member.userId === userId,
          )

          if (isStillMember) {
            const hasStillProduct = isStillMember.products.some(
              (p) => p === productName,
            )

            if (hasStillProduct) {
              throw new Error(
                `User ${userId} not removed from product ${productName}`,
              )
            }
          }
        }),
      )
      .subscribe({
        complete: () => {
          this.toasterService.show({
            type: 'success',
            message: `User removed successfully`,
          })
        },
        error: (erroResp: any) => {
          this.toasterService.show({
            type: 'error',
            message:
              erroResp?.error || erroResp?.message || 'Something went wrong',
          })
        },
      })
  }

  async openDialog(
    tpl: TemplateRef<any>,
    position: 'top' | 'middle' | 'bottom',
    userId: string,
    groupName: string,
    productName: string,
  ) {
    const closeEventResult =
      await this.modalService.open<DialogCloseEventWithResult>(tpl, {
        maxWidth: '650px',
        position,
      })

    switch (closeEventResult.event) {
      case 'confirmed':
        this.removeUserWorkflow(userId, groupName, productName)
        break
      default:
        break
    }
  }

  public async onScopeExpirePopoverOpen(
    event: { isFixed: boolean },
    member: MemberModel,
  ): Promise<void> {}

  public onScopeExpirePopoverClose(member: MemberModel) {}

  public clearExpiryAndClose(member: MemberModel, close: () => void) {
    console.log(`clearExpiryAndClose for: ${member.displayName} `)
    member.frontendState.scopesExpiry = {}
    member.frontendState.scopeExpiryDate = ''
  }

  public async saveExpiryAndClose(member: MemberModel, close: () => void) {
    if (!this.isExpiryInputValid(member)) return
    close()

    let dateUTC = ''
    if (member.frontendState.scopeExpiryDate !== '') {
      const date = new Date(member.frontendState.scopeExpiryDate)
      date.setMinutes(-date.getTimezoneOffset())
      dateUTC = date.toISOString()
    }

    member.scopesExpiry = Object.keys(member.frontendState.scopesExpiry)
      .filter((key) => member.frontendState.scopesExpiry[key])
      .map((scope) => ({
        scope: scope.replace(/^"|"$/g, ''),
        expiry: dateUTC,
      }))

    await this.updateUserWorkflow(member)
  }

  public isScopeExpirySet(member: MemberModel): boolean {
    return member.scopesExpiry.length > 0
  }

  public getFormatedDate(dateString: string): string {
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    return `${day}.${month}.${year}`
  }

  public isAdminCheck(member: MemberModel): boolean {
    return member.permissions.some(
      (p) => p === `${this.productOption().name}.admin`,
    )
  }

  private isExpiryInputValid(member: MemberModel): boolean {
    const areScopesSet = Object.values(member.frontendState.scopesExpiry).some(
      (v) => v,
    )
    const isDateSet = member.frontendState.scopeExpiryDate !== ''

    if ((areScopesSet && isDateSet) || (!areScopesSet && !isDateSet)) {
      return true
    }
    if (!areScopesSet) {
      this.toasterService.show({
        type: 'error',
        message: `Invalid: A scope has to be configured.`,
      })
    }
    if (!isDateSet) {
      this.toasterService.show({
        type: 'error',
        message: `Invalid: A date has to be configured.`,
      })
    }
    return false
  }
}
